import { IconButton } from 'components/CustomButton'
import CustomIcon from 'components/CustomIcon'
import { useEffect, useRef } from 'react'
import { useDrag, useDrop } from 'react-dnd'

const DraggableFocusItemCard = ({
  dragId,
  moveCard,
  findCard,
  children,
  onDelete,
  showDragHandle,
  onDrop,
  dragHandleOnly = true,
  dragDisabled = false,
}) => {
  const cardRef = useRef(null)
  const handleRef = useRef(null)

  const clientOffset = useRef({ x: 0, y: 0 })

  const originalIndex = findCard(dragId)?.index

  const [{ isDragging }, drag, preview] = useDrag({
    type: 'focusItem',
    canDrag: () => !dragDisabled,
    item: { dragId, originalIndex },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  const [, drop] = useDrop({
    accept: 'focusItem',
    hover({ dragId: draggedId }, monitor) {
      if (draggedId !== dragId) {
        const { index: hoverIndex } = findCard(dragId)
        moveCard(draggedId, hoverIndex)
      }
      const offset = monitor.getClientOffset()
      if (offset) clientOffset.current = { x: offset.x, y: offset.y }
    },
    drop: (item) => {
      moveCard(item.dragId, originalIndex)
      if (item.originalIndex !== originalIndex) {
        onDrop?.()
      }
    },
  })


  useEffect(() => {
    if (!isDragging) return
    let rafId
    const tick = () => {
      const el = cardRef.current
      if (!el) return

      // Vertical auto-scroll
      const verticalContainer = el.closest(
        '.overflow-auto, .overflow-y-auto, .overflow-scroll',
      )
      if (verticalContainer) {
        const { top, height } = verticalContainer.getBoundingClientRect()
        const relativeY = clientOffset.current.y - top
        const threshold = height * 0.25
        const speed = 10
        if (relativeY < threshold) {
          verticalContainer.scrollTop -= speed
        } else if (relativeY > height - threshold) {
          verticalContainer.scrollTop += speed
        }
      }

      // Horizontal auto-scroll (for horizontal lists)
      const horizontalContainer = el.closest('.horizontal')
      if (horizontalContainer) {
        const { left, width } = horizontalContainer.getBoundingClientRect()
        const relativeX = clientOffset.current.x - left
        const threshold = width * 0.2
        const speed = 10
        if (relativeX < threshold) {
          horizontalContainer.scrollLeft -= speed
        } else if (relativeX > width - threshold) {
          horizontalContainer.scrollLeft += speed
        }
      }

      rafId = requestAnimationFrame(tick)
    }
    rafId = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafId)
  }, [isDragging])

  drop(cardRef)
  if (!dragDisabled) {
    if (dragHandleOnly) {
      drag(handleRef)
    } else {
      drag(cardRef)
    }
  }
  // preview(getEmptyImage(), { captureDraggingState: true })

  preview(cardRef)

  return (
    <div
      onMouseDown={(e) => dragDisabled && e.preventDefault()}
      ref={cardRef}
      style={{ opacity: isDragging ? 0.3 : 1 }}
      className={`relative ${dragHandleOnly ? 'p-4 pt-8' : 'p-2'} mb-4 border bg-secondary-100 rounded-md ${dragDisabled && 'pointer-events-none'}`}
    >
      <div className="absolute top-2 right-2 flex items-center justify-center space-x-1">
        {showDragHandle && dragHandleOnly && (
          <span
            ref={handleRef}
            onMouseDown={(e) => {
              dragDisabled && e.preventDefault()
              e.stopPropagation()
            }}
            className="cursor-grab active:cursor-grabbing p-1 flex items-center justify-center"
          >
            <CustomIcon className="text-rand-200" icon="drag_indicator" />
          </span>
        )}
        {!!onDelete && <IconButton onClick={onDelete} />}
      </div>
      {children}
    </div>
  )
}

export default DraggableFocusItemCard
